import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * Page identity tier (PRO-0260, DS-1). Renders the route heading + supporting copy
 * in the identity type scale. Used by Home and ComposeSurface. The heading is the
 * focus target the PRO-0250 route-change focus manager moves to (`useRouteTitle`
 * queries the first `h1, h2` in `main`), so the level and id are caller-controlled.
 */
export interface PageHeaderProps {
  title: string;
  intro?: ReactNode;
  meta?: ReactNode;
  /**
   * Optional action cluster (e.g. a primary page action or a settings switch).
   * Stacks under the title below `sm` so a wide control cannot crush the identity
   * copy into a single-character column; sits opposite the title from `sm` up.
   */
  actions?: ReactNode;
  headingId?: string;
  as?: 'h1' | 'h2';
}

export function PageHeader({ title, intro, meta, actions, headingId, as = 'h1' }: PageHeaderProps) {
  const Heading = as;
  return (
    <div
      className={cn(
        'mb-6',
        actions
          ? 'flex flex-col items-stretch gap-4 sm:flex-row sm:items-start sm:justify-between'
          : undefined,
      )}
    >
      <div className="min-w-0">
        <Heading id={headingId} tabIndex={-1} className="m-0 text-xl font-bold text-text-primary outline-none">
          {title}
        </Heading>
        {intro ? <p className="mt-2 text-base text-text-secondary">{intro}</p> : null}
        {meta ? <div className="mt-1 text-sm text-text-secondary">{meta}</div> : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-2 sm:justify-end">{actions}</div>
      ) : null}
    </div>
  );
}
